import { broadcastChannelLogoutEvent, getKeycloakInstance } from './keycloakConfig';

export const ONE_TIME_LOGIN_KEY = '_otl';
const isDevMode = import.meta.env.VITE_ENVIRONMENT === 'dev';

export const loginTokenStorage = sessionStorage;
export const broadcastChannel =
	typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('broadcastLogout') : undefined;

export const addQueryParamsToUrl = (url: string, params: Record<string, string | null | undefined>) => {
	const urlObject = new URL(url);
	for (const [key, value] of Object.entries(params)) {
		if (value) urlObject.searchParams.set(key, value);
	}
	return urlObject.toString();
};

export const extractOneTimeLoginFromHash = (url: string): string | null => {
	try {
		const urlObj = new URL(url);
		const hash = urlObj.hash.substring(1);
		if (!hash) return null;
		const params = new URLSearchParams(hash);
		return params.get(ONE_TIME_LOGIN_KEY);
	} catch (error) {
		console.error(error);
		return null;
	}
};

function getJwtPayload(token: string) {
	try {
		const parts = token.split('.');
		if (parts.length !== 3) throw new Error('Invalid JWT token');
		const payloadBase64 = parts[1];
		const payloadJson = window.atob(payloadBase64);
		return JSON.parse(payloadJson);
	} catch (error) {
		console.error('Error parsing JWT payload:', error);
		return null;
	}
}

export const getOneTimeLoginToken = () => loginTokenStorage.getItem(ONE_TIME_LOGIN_KEY);
export const setOneTimeLoginToken = (token: string) => loginTokenStorage.setItem(ONE_TIME_LOGIN_KEY, token);
export const removeOneTimeLoginToken = () => loginTokenStorage.removeItem(ONE_TIME_LOGIN_KEY);
export const getOneTimeLoginTokenPayload = () => getJwtPayload(getOneTimeLoginToken() ?? '');


export const logoutKeycloak = ({ isUseBroadcast }: { isUseBroadcast?: boolean } = { isUseBroadcast: true }) => {
	isUseBroadcast && broadcastChannel?.postMessage(broadcastChannelLogoutEvent);
	const otl = getOneTimeLoginToken();
	if (otl) {
		removeOneTimeLoginToken();
		location.reload();
		return;
	}
	const kc = getKeycloakInstance();
	if (kc) kc.logout();
};

export const updateKeycloakToken = () => {
	const kc = getKeycloakInstance();
	if (!kc) return;
	kc
		.updateToken(60)
		.then((refreshed) => {
			if (refreshed) console.log('Token was refreshed');
		})
		.catch((error) => {
			console.error('Token refresh failed:', error);
			logoutKeycloak();
		});
};

export const getAuthToken = () => {
	const otlToken = getOneTimeLoginToken();
	if (otlToken || isDevMode) return otlToken;
	return getKeycloakInstance()?.token ?? null;
};

const redeemRequests = new Map<string, Promise<string | null>>();

export const getRedeemToken = async (otlToken: string): Promise<string | null> => {
	if (!otlToken) return null;

	const tokenInStorage = getOneTimeLoginToken();
	if (tokenInStorage) return tokenInStorage;

	const existing = redeemRequests.get(otlToken);
	if (existing) return existing;

	const promise = (async () => {
		try {
			const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
			const response = await fetch(`${baseUrl}/api/auth/redeem-token/${encodeURIComponent(otlToken)}/`, {
				headers: { Accept: 'application/json' },
			});

			if (!response.ok) {
				console.error('Redeem token failed, status:', response.status);
				return null;
			}

			const data = await response.json();
			const access = data?.access_token ?? null;
			if (access) {
				setOneTimeLoginToken(access);
				return access;
			}

			console.error('Redeem token: access_token missing in response', data);
			return null;
		} catch (error) {
			console.error('Error redeeming token:', error);
			return null;
		} finally {
			redeemRequests.delete(otlToken);
		}
	})();

	redeemRequests.set(otlToken, promise);
	return promise;
};

